import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, tokenStore, API_BASE } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Users, Search, AlertTriangle, MapPin, Power, PowerOff, Hash, CheckCircle2, XCircle, Clock3, Download, Upload } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  name: "",
  code: "",
  address: "",
  district: "",
  latitude: "",
  longitude: "",
  geofence_radius_m: 500,
  worker_start_time: "09:00",
  worker_end_time: "17:00",
  approved_sweeper: 0,
  approved_driver: 0,
  approved_supervisor: 0,
  land_extent_acres: "",
  hourly_selfie_interval_minutes: "",
};

export default function ParksPage() {
  const [parks, setParks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [freedSlots, setFreedSlots] = useState([]);
  const [nextCodeInfo, setNextCodeInfo] = useState({ next_code: "01", from_freed_slot: false });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  // Bulk import states
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkExcel, setBulkExcel] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, e, f, n] = await Promise.all([
        api.get("/parks"),
        api.get("/employees"),
        api.get("/admin/parks/freed-codes"),
        api.get("/parks/next-code"),
      ]);
      setParks(p.data);
      setEmployees(e.data || []);
      setFreedSlots(f.data || []);
      setNextCodeInfo(n.data || { next_code: "01" });
    } catch {
      toast.error("Failed to load parks");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const downloadTemplate = async () => {
    try {
      const tk = tokenStore.get();
      const res = await fetch(`${API_BASE}/parks/template`, { headers: { Authorization: `Bearer ${tk}` } });
      if (!res.ok) return toast.error("Template download failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "parks_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Excel template downloaded");
    } catch { toast.error("Template download failed"); }
  };

  const submitBulk = async () => {
    if (!bulkExcel) return toast.error("Excel file required");
    setBulkBusy(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append("excel", bulkExcel);
      const { data } = await api.post("/parks/bulk_import", fd);
      setBulkResult(data);
      toast.success(`Imported ${data.imported}, skipped ${data.skipped}`);
      load();
    } catch (e) { toast.error(errorMsg(e, "Bulk import failed")); }
    finally { setBulkBusy(false); }
  };

  // ----- per-park headcounts
  const headcounts = useMemo(() => {
    const m = {};
    for (const e of employees) {
      const pid = e.park_id;
      if (!pid) continue;
      if (!m[pid]) m[pid] = { Sweeper: 0, Driver: 0, Supervisor: 0, reliever: 0 };
      const cat = e.designation;
      if (e.is_reliever) m[pid].reliever += 1;
      if (cat === "Sweeper" || cat === "Driver" || cat === "Supervisor") {
        m[pid][cat] += 1;
      }
    }
    return m;
  }, [employees]);

  const parkStatus = useCallback((p) => {
    const hc = headcounts[p.id] || { Sweeper: 0, Driver: 0, Supervisor: 0, reliever: 0 };
    const cats = [
      { actual: hc.Sweeper, approved: p.approved_sweeper || 0 },
      { actual: hc.Driver, approved: p.approved_driver || 0 },
      { actual: hc.Supervisor, approved: p.approved_supervisor || 0 },
    ];
    const excess = cats.some((c) => c.actual > c.approved);
    const shortAny = cats.some((c) => c.actual < c.approved && c.approved > 0);
    return { hc, excess, shortAny };
  }, [headcounts]);

  const openCreate = () => {
    setEditing(null);
    // Auto-fill code with next suggested value
    setForm({ ...emptyForm, code: nextCodeInfo.next_code });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", code: p.code || "", address: p.address || "", district: p.district || "",
      latitude: p.latitude ?? "", longitude: p.longitude ?? "", geofence_radius_m: p.geofence_radius_m ?? 500,
      worker_start_time: p.worker_start_time || "09:00",
      worker_end_time: p.worker_end_time || "17:00",
      approved_sweeper: p.approved_sweeper ?? 0,
      approved_driver: p.approved_driver ?? 0,
      approved_supervisor: p.approved_supervisor ?? 0,
      land_extent_acres: p.land_extent_acres ?? "",
      hourly_selfie_interval_minutes: p.hourly_selfie_interval_minutes ?? "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name || !form.code) { toast.error("Name and code are required"); return; }
    if (form.worker_start_time && form.worker_end_time && form.worker_start_time >= form.worker_end_time) {
      toast.error("Worker start time must be before end time"); return;
    }
    setSaving(true);
    const payload = {
      ...form,
      code: String(form.code).trim().padStart(2, "0"),
      latitude: form.latitude === "" ? null : Number(form.latitude),
      longitude: form.longitude === "" ? null : Number(form.longitude),
      geofence_radius_m: Number(form.geofence_radius_m) || 500,
      worker_start_time: form.worker_start_time || null,
      worker_end_time: form.worker_end_time || null,
      approved_sweeper: Math.max(0, parseInt(form.approved_sweeper, 10) || 0),
      approved_driver: Math.max(0, parseInt(form.approved_driver, 10) || 0),
      approved_supervisor: Math.max(0, parseInt(form.approved_supervisor, 10) || 0),
      land_extent_acres: form.land_extent_acres === "" ? null : Number(form.land_extent_acres),
      hourly_selfie_interval_minutes: form.hourly_selfie_interval_minutes === "" || form.hourly_selfie_interval_minutes == null
        ? null
        : Math.max(5, Math.min(180, parseInt(form.hourly_selfie_interval_minutes, 10) || 60)),
    };
    try {
      if (editing) {
        await api.put(`/parks/${editing.id}`, payload);
        toast.success("Park updated");
      } else {
        await api.post("/parks", payload);
        toast.success(`Park created with code ${payload.code}`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (p) => {
    const next = !p.active;
    try {
      await api.patch(`/parks/${p.id}/active`, { active: next });
      setParks((prev) => prev.map((x) => (x.id === p.id ? { ...x, active: next } : x)));
      toast.success(next ? `${p.name} turned ON` : `${p.name} turned OFF`);
    } catch {
      toast.error("Toggle failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(
      `Delete park '${p.name}' (code ${p.code})?\n\nThe code ${p.code} will be moved to "pending approval" — a new park can only reuse this number after you explicitly approve it.\n\nIf you only want to temporarily disable the park, use the ON/OFF switch instead.`
    )) return;
    try {
      await api.delete(`/parks/${p.id}`);
      toast.success(`Park deleted. Code ${p.code} → pending approval.`);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const approveSlot = async (code) => {
    try {
      await api.post(`/admin/parks/freed-codes/${code}/approve`);
      toast.success(`Code ${code} approved — next park will use it`);
      load();
    } catch {
      toast.error("Approval failed");
    }
  };

  const discardSlot = async (code) => {
    if (!window.confirm(`Discard freed code ${code}? It will not be reused.`)) return;
    try {
      await api.delete(`/admin/parks/freed-codes/${code}`);
      toast.success(`Code ${code} discarded`);
      load();
    } catch {
      toast.error("Discard failed");
    }
  };

  // ---------------------------------------------------------- search filter
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = parks;
    if (q) {
      list = list.filter((p) =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.code || "").toLowerCase().includes(q) ||
        (p.district || "").toLowerCase().includes(q)
      );
    }
    // sort by numeric code so the table reads 01, 02, 03…
    return [...list].sort((a, b) => {
      const ca = parseInt(a.code, 10); const cb = parseInt(b.code, 10);
      if (!isNaN(ca) && !isNaN(cb)) return ca - cb;
      return String(a.code).localeCompare(String(b.code));
    });
  }, [parks, search]);

  const aggregate = useMemo(() => {
    let approved = 0, actual = 0, excess = 0;
    for (const p of parks) {
      if (p.active === false) continue;
      const st = parkStatus(p);
      approved += (p.approved_sweeper || 0) + (p.approved_driver || 0) + (p.approved_supervisor || 0);
      actual += st.hc.Sweeper + st.hc.Driver + st.hc.Supervisor;
      if (st.excess) excess += 1;
    }
    return { approved, actual, excess };
  }, [parks, parkStatus]);

  const inactiveCount = parks.filter((p) => p.active === false).length;
  const pendingFreed = freedSlots.filter((s) => s.status === "pending_approval");
  const approvedFreed = freedSlots.filter((s) => s.status === "approved");

  return (
    <AppShell title="Parks" subtitle="Industrial parks under PMC monitoring" action={
      <div className="flex gap-2">
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" data-testid="parks-bulk-import-trigger">
              <Upload className="mr-2 h-4 w-4" />Import Excel
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Bulk import industrial parks</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3 py-2">
              <Button variant="outline" onClick={downloadTemplate} data-testid="parks-template-download">
                <Download className="mr-2 h-4 w-4" />Download Excel template
              </Button>
              <div className="rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-[11px] leading-relaxed text-amber-900" data-testid="parks-bulk-hint">
                <span className="font-semibold">Park setup</span> &mdash;
                Specify 2-digit <strong>Park Code</strong> (e.g. <code className="px-1 rounded bg-white">01</code>), <strong>Park Name</strong>, District, and approved manpower headcounts for Sweeper, Driver, and Supervisor.
              </div>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Excel file (.xlsx) *</Label>
                <Input type="file" accept=".xlsx" onChange={(e)=>setBulkExcel(e.target.files?.[0])} data-testid="parks-bulk-excel-input" />
              </div>
              {bulkResult && (
                <div className="rounded-lg border p-3">
                  <div className="text-sm">
                    Imported <b>{bulkResult.imported}</b>
                    {bulkResult.updated > 0 ? ` (inserted: ${bulkResult.inserted}, updated: ${bulkResult.updated})` : ""}
                    , skipped <b>{bulkResult.skipped}</b>
                  </div>
                  {bulkResult.errors?.length > 0 && (
                    <details className="mt-2 text-xs">
                      <summary className="cursor-pointer text-muted-foreground">{bulkResult.errors.length} errors</summary>
                      <ul className="mt-1 list-disc pl-5">
                        {bulkResult.errors.slice(0, 30).map((er, i) => <li key={i}>{er}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={()=>setBulkOpen(false)}>Close</Button>
              <Button onClick={submitBulk} disabled={bulkBusy} data-testid="parks-bulk-submit">
                <Upload className="mr-2 h-4 w-4" />{bulkBusy ? "Importing…" : "Import"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="parks-add-button"><Plus className="mr-2 h-4 w-4" />Add park</Button>
          </DialogTrigger>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit park ${form.code}` : "New park"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Code *">
              <div className="flex items-center gap-2">
                <Input value={form.code} onChange={(e)=>setForm({...form,code:e.target.value})} className="w-24 font-mono" data-testid="park-code-input" />
                {!editing && (
                  <span className="text-[11px] text-muted-foreground">
                    {nextCodeInfo.from_freed_slot
                      ? <>auto-suggested from <b>freed slot</b></>
                      : <>auto-suggested as next in sequence</>}
                  </span>
                )}
              </div>
            </Field>
            <Field label="Name *"><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="park-name-input" /></Field>
            <Field label="District" full><Input value={form.district} onChange={(e)=>setForm({...form,district:e.target.value})} /></Field>
            <Field label="Address" full><Input value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} /></Field>
            <Field label="Latitude"><Input type="number" step="0.000001" value={form.latitude} onChange={(e)=>setForm({...form,latitude:e.target.value})} data-testid="park-lat-input" /></Field>
            <Field label="Longitude"><Input type="number" step="0.000001" value={form.longitude} onChange={(e)=>setForm({...form,longitude:e.target.value})} data-testid="park-lng-input" /></Field>
            <Field label="Geofence radius (m)" full><Input type="number" value={form.geofence_radius_m} onChange={(e)=>setForm({...form,geofence_radius_m:e.target.value})} data-testid="park-radius-input" /></Field>

            <div className="col-span-2 mt-1 rounded-md border bg-secondary/30 p-3">
              <Label className="text-xs text-muted-foreground">Worker hours at this park</Label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <Field label="From"><Input type="time" value={form.worker_start_time} onChange={(e)=>setForm({...form,worker_start_time:e.target.value})} data-testid="park-worker-start" /></Field>
                <Field label="To"><Input type="time" value={form.worker_end_time} onChange={(e)=>setForm({...form,worker_end_time:e.target.value})} data-testid="park-worker-end" /></Field>
              </div>
            </div>

            <div className="col-span-2 mt-1 rounded-md border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-amber-700" />
                <Label className="text-xs font-semibold text-amber-900">SIPCOT Approved Manpower</Label>
              </div>
              <p className="mt-0.5 text-[11px] text-amber-800/80">Only this head-count is payable. Workers beyond these counts are flagged as Relievers.</p>
              <div className="mt-2 grid grid-cols-3 gap-3">
                <Field label="Sweeper"><Input type="number" min={0} value={form.approved_sweeper} onChange={(e)=>setForm({...form,approved_sweeper:e.target.value})} data-testid="park-approved-sweeper" /></Field>
                <Field label="Driver"><Input type="number" min={0} value={form.approved_driver} onChange={(e)=>setForm({...form,approved_driver:e.target.value})} data-testid="park-approved-driver" /></Field>
                <Field label="Supervisor"><Input type="number" min={0} value={form.approved_supervisor} onChange={(e)=>setForm({...form,approved_supervisor:e.target.value})} data-testid="park-approved-supervisor" /></Field>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3">
                <Field label="Land Extent (acres) — optional, used in monthly DOCX report"><Input type="number" step="0.01" min={0} value={form.land_extent_acres} onChange={(e)=>setForm({...form,land_extent_acres:e.target.value})} placeholder="e.g. 1478.35" data-testid="park-land-extent" /></Field>
                <Field label="Hourly self-attendance interval (minutes) — leave blank for global default">
                  <Input
                    type="number"
                    min={5}
                    max={180}
                    step={5}
                    value={form.hourly_selfie_interval_minutes}
                    onChange={(e)=>setForm({...form,hourly_selfie_interval_minutes:e.target.value})}
                    placeholder="e.g. 30 (busy park) or 90 (remote park)"
                    data-testid="park-hourly-interval"
                  />
                </Field>
              </div>
            </div>

            <div className="col-span-2 mt-1 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3 text-[11px] text-slate-600">
              <b>Salary:</b> workers at this park draw a <b>monthly salary</b> (not daily wages). The salary structure will be configured in a future <i>Payroll Settings</i> screen.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="park-save-button">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  }>
      {/* Summary strip */}
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="parks-summary-strip">
        <Stat label="Parks" value={parks.length} icon={MapPin} />
        <Stat label="Approved headcount" value={aggregate.approved} icon={Users} />
        <Stat label="Actual headcount" value={aggregate.actual} icon={Users} />
        <Stat label="Inactive" value={inactiveCount} icon={PowerOff} accent={inactiveCount > 0 ? "slate" : "slate"} />
      </div>

      {/* Freed codes panel */}
      {(pendingFreed.length > 0 || approvedFreed.length > 0) && (
        <Card className="mb-3 border-amber-300 bg-amber-50/60" data-testid="parks-freed-panel">
          <CardContent className="flex flex-wrap items-center gap-3 p-3">
            <Hash className="h-4 w-4 text-amber-700" />
            <div className="text-[12px] font-semibold uppercase tracking-wider text-amber-900">Freed park codes</div>
            <div className="flex flex-wrap gap-1.5">
              {pendingFreed.map((s) => (
                <div key={s.code} className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[11px]">
                  <Clock3 className="h-3 w-3 text-amber-700" />
                  <span className="font-mono font-semibold text-amber-900">{s.code}</span>
                  <span className="text-amber-800/80">from {s.freed_from_park_name || "—"}</span>
                  <button onClick={() => approveSlot(s.code)} className="ml-1 inline-flex items-center gap-0.5 rounded bg-emerald-600 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-emerald-700" data-testid={`approve-freed-${s.code}`}>
                    <CheckCircle2 className="h-3 w-3" />Approve
                  </button>
                  <button onClick={() => discardSlot(s.code)} className="inline-flex items-center gap-0.5 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-300" data-testid={`discard-freed-${s.code}`}>
                    <XCircle className="h-3 w-3" />Discard
                  </button>
                </div>
              ))}
              {approvedFreed.map((s) => (
                <div key={s.code} className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[11px]">
                  <CheckCircle2 className="h-3 w-3 text-emerald-700" />
                  <span className="font-mono font-semibold text-emerald-800">{s.code}</span>
                  <span className="text-emerald-700/80">approved — next park will use this</span>
                  <button onClick={() => discardSlot(s.code)} className="ml-1 inline-flex items-center gap-0.5 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700 hover:bg-slate-300">
                    <XCircle className="h-3 w-3" />Drop
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search by park name, code, or district…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-8" data-testid="parks-search-input" />
        </div>
        <div className="text-xs text-muted-foreground">{filtered.length} of {parks.length} parks</div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : parks.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><div className="text-sm text-muted-foreground">No parks yet.</div><Button className="mt-3" onClick={openCreate}>Add first park</Button></CardContent></Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="parks-table">
              <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                <tr className="border-b border-slate-200">
                  <Th>#</Th>
                  <Th className="text-left">Park</Th>
                  <Th className="text-left">District</Th>
                  <Th>Sweeper</Th>
                  <Th>Driver</Th>
                  <Th>Supervisor</Th>
                  <Th>Total</Th>
                  <Th>Reliever</Th>
                  <Th>Status</Th>
                  <Th>On/Off</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={11} className="px-3 py-6 text-center text-sm text-muted-foreground">No parks match your search.</td></tr>
                )}
                {filtered.map((p) => {
                  const st = parkStatus(p);
                  const approvedTotal = (p.approved_sweeper || 0) + (p.approved_driver || 0) + (p.approved_supervisor || 0);
                  const actualTotal = st.hc.Sweeper + st.hc.Driver + st.hc.Supervisor;
                  const rowCls = p.active === false
                    ? "bg-slate-100 text-slate-500 border-l-2 border-l-slate-400 hover:bg-slate-200"
                    : st.excess
                      ? "bg-amber-100 hover:bg-amber-200/80 border-l-2 border-l-amber-500"
                      : st.shortAny
                        ? "bg-rose-100/80 hover:bg-rose-200/70 border-l-2 border-l-rose-400"
                        : "hover:bg-slate-50";
                  return (
                    <tr key={p.id} className={`border-b border-slate-100 ${rowCls}`} data-testid={`park-row-${p.code}`}>
                      <Td><span className="font-mono text-sm font-semibold tabular-nums text-slate-700">{p.code}</span></Td>
                      <Td className="text-left">
                        <button onClick={() => openEdit(p)} className="text-left font-medium text-slate-900 hover:text-amber-700">{p.name}</button>
                        {p.active === false && <span className="ml-2 rounded-full bg-slate-300 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-slate-700">off</span>}
                      </Td>
                      <Td className="text-left text-xs text-slate-700">{p.district || "—"}</Td>

                      <Td><CatCell actual={st.hc.Sweeper} approved={p.approved_sweeper || 0} /></Td>
                      <Td><CatCell actual={st.hc.Driver} approved={p.approved_driver || 0} /></Td>
                      <Td><CatCell actual={st.hc.Supervisor} approved={p.approved_supervisor || 0} /></Td>
                      <Td><CatCell actual={actualTotal} approved={approvedTotal} bold /></Td>

                      <Td>{st.hc.reliever > 0 ? (
                        <span className="rounded-full bg-slate-200 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-slate-800">{st.hc.reliever}</span>
                      ) : <span className="text-slate-400">—</span>}</Td>

                      <Td>
                        {p.active === false ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">Inactive</span>
                        ) : st.excess ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-300 px-1.5 py-0.5 text-[10px] font-semibold text-amber-950" data-testid={`park-status-${p.code}`}>
                            <AlertTriangle className="h-3 w-3" />Excess
                          </span>
                        ) : st.shortAny ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-300 px-1.5 py-0.5 text-[10px] font-semibold text-rose-950" data-testid={`park-status-${p.code}`}>Short</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900" data-testid={`park-status-${p.code}`}>OK</span>
                        )}
                      </Td>

                      <Td>
                        <div className="flex items-center justify-center gap-1.5">
                          <Switch
                            checked={p.active !== false}
                            onCheckedChange={() => toggleActive(p)}
                            data-testid={`park-active-toggle-${p.code}`}
                            aria-label={`Toggle ${p.name}`}
                          />
                          {p.active !== false
                            ? <Power className="h-3.5 w-3.5 text-emerald-600" />
                            : <PowerOff className="h-3.5 w-3.5 text-slate-500" />}
                        </div>
                      </Td>

                      <Td>
                        <div className="flex justify-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`park-edit-${p.code}`}><Pencil className="h-3.5 w-3.5" /></Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600 hover:bg-rose-50" onClick={() => remove(p)} data-testid={`park-delete-${p.code}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3 border-t bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-amber-100 ring-1 ring-amber-500" />Excess (relievers in roster)</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-rose-100 ring-1 ring-rose-400" />Short of approved</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-emerald-100 ring-1 ring-emerald-300" />Fully staffed</span>
            <span className="flex items-center gap-1.5"><span className="inline-block h-2 w-3 rounded bg-slate-200 ring-1 ring-slate-400" />Inactive</span>
            <span className="ml-auto text-slate-500">Each manpower cell shows <span className="font-mono">actual / approved</span></span>
          </div>
        </Card>
      )}
    </AppShell>
  );
}

/* ----------------------------------------------- helper sub-components */
function Th({ children, className = "" }) {
  return <th className={`whitespace-nowrap px-3 py-2 text-center text-[11px] font-semibold ${className}`}>{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`whitespace-nowrap px-3 py-2 text-center align-middle ${className}`}>{children}</td>;
}
function CatCell({ actual, approved, bold = false }) {
  const isExcess = actual > approved;
  const isShort = approved > 0 && actual < approved;
  const cls = isExcess ? "text-amber-800" : isShort ? "text-rose-800" : "text-slate-800";
  return (
    <span className={`font-mono tabular-nums ${cls} ${bold ? "font-semibold" : ""}`}>
      <span className={bold ? "text-sm" : "text-xs"}>{actual}</span>
      <span className="text-slate-400"> / </span>
      <span className={`${bold ? "text-sm" : "text-xs"} text-slate-500`}>{approved}</span>
    </span>
  );
}
function Stat({ label, value, icon: Icon, accent = "amber" }) {
  const ring = accent === "amber" ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-slate-50/60";
  const iconCls = accent === "amber" ? "text-amber-700" : "text-slate-500";
  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 ${ring}`}>
      <Icon className={`h-4 w-4 shrink-0 ${iconCls}`} />
      <div className="min-w-0">
        <div className="text-[10px] font-medium uppercase tracking-wider text-slate-600">{label}</div>
        <div className="font-mono text-base font-semibold tabular-nums text-slate-900">{value}</div>
      </div>
    </div>
  );
}
function Field({ label, children, full }) {
  return (
    <div className={`flex flex-col gap-1 ${full ? "col-span-2" : ""}`}>
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
